import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Alert,
  Chip,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  FormLabel,
  RadioGroup,
  Radio,
  FormControlLabel,
} from '@mui/material';
import { default as GridLegacy } from '@mui/material/Grid';
import {
  Add as AddIcon,
  TodayOutlined as TodayIcon,
  CalendarMonth as CalendarIcon,
  CheckCircle as CheckIcon,
} from '@mui/icons-material';
import api from '../services/api';

interface Sale {
  Date?: string;
  'Customer Name '?: string;
  'Customer Name'?: string;
  customerName?: string;
  'Contact Number '?: string;
  'Contact Number'?: string;
  contactNumber?: string;
  Email?: string;
  email?: string;
  Product?: string;
  product?: string;
}

interface FormData {
  date: string;
  customerName: string;
  contactNumber: string;
  email: string;
  product: string;
}

type FilterType = 'today' | 'week' | 'month';

const MySalesTracker: React.FC = () => {
  const [sales, setSales] = useState<Sale[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [filter, setFilter] = useState<FilterType>('today');
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  // Intake form reminder — required to submit, but NOT saved anywhere
  const [intakeCompleted, setIntakeCompleted] = useState<string>('');

  // Helper function to get today's date in Pacific Time
  const getTodayInPacificTime = (): string => {
    const now = new Date();
    const pacificTime = new Date(now.toLocaleString('en-US', { timeZone: 'America/Los_Angeles' }));
    const year = pacificTime.getFullYear();
    const month = String(pacificTime.getMonth() + 1).padStart(2, '0');
    const day = String(pacificTime.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Form state
  const [formData, setFormData] = useState<FormData>({
    date: getTodayInPacificTime(),
    customerName: '',
    contactNumber: '',
    email: '',
    product: '',
  });

  // Product options
  const products: string[] = [
    'Semaglutide Injections',
    'Semaglutide Sublinguals',
    'Tirzep Injections',
    'Tirzep Sublinguals',
    'NAD+',
    'DirectMax',
    'HRT',
    'GLP-1 Discount 1 Month',
    'NAD+ Discount 1 Month',
  ];

  useEffect(() => {
    fetchSales();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const fetchSales = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await api.getMySales(filter);
      setSales(response.sales || []);
    } catch (error: any) {
      console.error('Error fetching sales:', error);
      setError(error.response?.data?.error || 'Failed to fetch sales');
      setSales([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (
    _event: React.MouseEvent<HTMLElement>,
    newFilter: FilterType | null
  ) => {
    if (newFilter !== null) {
      setFilter(newFilter);
    }
  };

  const handleDialogOpen = () => {
    setFormData({
      date: getTodayInPacificTime(),
      customerName: '',
      contactNumber: '',
      email: '',
      product: '',
    });
    setIntakeCompleted('');
    setDialogOpen(true);
    setError('');
    setSuccess('');
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setError('');
    setSuccess('');
  };

  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSelectChange = (field: keyof FormData) => (event: SelectChangeEvent<string>) => {
    handleInputChange(field, event.target.value);
  };

  const handleSubmit = async () => {
    if (
      !formData.date ||
      !formData.customerName ||
      !formData.contactNumber ||
      !formData.email ||
      !formData.product
    ) {
      setError('Please fill in all fields');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('Please enter a valid email address');
      return;
    }

    // Intake form reminder — must be answered, but not saved anywhere
    if (!intakeCompleted) {
      setError('Please confirm whether the Intake Form was completed');
      return;
    }

    try {
      setSubmitting(true);
      setError('');
      await api.createMySale(formData);

      setSuccess('Sale added successfully!');
      setTimeout(() => {
        handleDialogClose();
        fetchSales();
      }, 1500);
    } catch (error: any) {
      console.error('Error adding sale:', error);
      setError(error.response?.data?.error || 'Failed to add sale');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateStr?: string): string => {
    if (!dateStr) return 'N/A';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    } catch (e) {
      return dateStr;
    }
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, color: '#61ABBC' }}>
            📊 My Sales Tracker
          </Typography>
          <Typography variant="body1" color="text.secondary">
            View and manage your personal sales
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleDialogOpen}
          sx={{ bgcolor: '#61ABBC', '&:hover': { bgcolor: '#4B88A2' } }}
        >
          Add Sale
        </Button>
      </Box>

      {/* Date Filter */}
      <Box sx={{ mb: 3 }}>
        <ToggleButtonGroup
          value={filter}
          exclusive
          onChange={handleFilterChange}
          aria-label="date filter"
        >
          <ToggleButton value="today" aria-label="today">
            <TodayIcon sx={{ mr: 1 }} />
            Today
          </ToggleButton>
          <ToggleButton value="week" aria-label="week">
            <CalendarIcon sx={{ mr: 1 }} />
            This Week
          </ToggleButton>
          <ToggleButton value="month" aria-label="month">
            <CalendarIcon sx={{ mr: 1 }} />
            This Month
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Sales Table */}
      <Card>
        <CardContent>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : error && sales.length === 0 ? (
            <Alert severity="error">{error}</Alert>
          ) : sales.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="h6" color="text.secondary">
                No sales found for this period
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Click "Add Sale" to record your first sale
              </Typography>
            </Box>
          ) : (
            <>
              <Box
                sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}
              >
                <Typography variant="h6">Sales ({sales.length})</Typography>
                <Chip
                  label={`${
                    filter === 'today'
                      ? "Today's"
                      : filter === 'week'
                      ? "This Week's"
                      : "This Month's"
                  } Sales`}
                  color="primary"
                  size="small"
                />
              </Box>
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                      <TableCell><strong>Date</strong></TableCell>
                      <TableCell><strong>Customer Name</strong></TableCell>
                      <TableCell><strong>Contact Number</strong></TableCell>
                      <TableCell><strong>Email</strong></TableCell>
                      <TableCell><strong>Product</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sales.map((sale, index) => (
                      <TableRow key={index} hover>
                        <TableCell>{formatDate(sale.Date)}</TableCell>
                        <TableCell>
                          {sale['Customer Name '] ||
                            sale['Customer Name'] ||
                            sale.customerName ||
                            'N/A'}
                        </TableCell>
                        <TableCell>
                          {sale['Contact Number '] ||
                            sale['Contact Number'] ||
                            sale.contactNumber ||
                            'N/A'}
                        </TableCell>
                        <TableCell>{sale.Email || sale.email || 'N/A'}</TableCell>
                        <TableCell>{sale.Product || sale.product || 'N/A'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </CardContent>
      </Card>

      {/* Add Sale Dialog */}
      <Dialog open={dialogOpen} onClose={handleDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <AddIcon sx={{ mr: 1, color: '#61ABBC' }} />
            Add New Sale
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <GridLegacy container spacing={2} sx={{ mt: 0.5 }}>
            {/* Date */}
            <GridLegacy size={{ xs: 12 }}>
              <TextField
                label="Date"
                type="date"
                fullWidth
                value={formData.date}
                onChange={(e) => handleInputChange('date', e.target.value)}
                InputLabelProps={{ shrink: true }}
                required
              />
            </GridLegacy>

            {/* Customer Name */}
            <GridLegacy size={{ xs: 12 }}>
              <TextField
                label="Customer Name"
                fullWidth
                value={formData.customerName}
                onChange={(e) => handleInputChange('customerName', e.target.value)}
                required
              />
            </GridLegacy>

            {/* Contact Number */}
            <GridLegacy size={{ xs: 12 }}>
              <TextField
                label="Contact Number"
                fullWidth
                value={formData.contactNumber}
                onChange={(e) => handleInputChange('contactNumber', e.target.value)}
                placeholder="(555) 123-4567"
                required
              />
            </GridLegacy>

            {/* Email */}
            <GridLegacy size={{ xs: 12 }}>
              <TextField
                label="Email"
                type="email"
                fullWidth
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                placeholder="customer@example.com"
                required
              />
            </GridLegacy>

            {/* Product */}
            <GridLegacy size={{ xs: 12 }}>
              <FormControl fullWidth required>
                <InputLabel>Product</InputLabel>
                <Select
                  value={formData.product}
                  label="Product"
                  onChange={handleSelectChange('product')}
                >
                  {products.map((product) => (
                    <MenuItem key={product} value={product}>
                      {product}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </GridLegacy>

            {/* Intake Form Completed — reminder only, not saved */}
            <GridLegacy size={{ xs: 12 }}>
              <FormControl required error={!!error && !intakeCompleted}>
                <FormLabel id="intake-form-completed-label">
                  Intake Form Completed?
                </FormLabel>
                <RadioGroup
                  row
                  aria-labelledby="intake-form-completed-label"
                  name="intake-form-completed"
                  value={intakeCompleted}
                  onChange={(e) => setIntakeCompleted(e.target.value)}
                >
                  <FormControlLabel value="yes" control={<Radio />} label="Yes" />
                  <FormControlLabel value="no" control={<Radio />} label="No" />
                </RadioGroup>
              </FormControl>
            </GridLegacy>
          </GridLegacy>

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" icon={<CheckIcon />} sx={{ mt: 2 }}>
              {success}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={submitting || !intakeCompleted}
            sx={{ bgcolor: '#61ABBC', '&:hover': { bgcolor: '#4B88A2' } }}
          >
            {submitting ? <CircularProgress size={24} /> : 'Add Sale'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default MySalesTracker;
